import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { FiCheckCircle, FiAlertCircle, FiLoader, FiMail, FiFileText, FiHome } from 'react-icons/fi';

const PaymentSuccess = () => {
  const location = useLocation();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const dealerId = params.get('dealer_id');
    const shedId = params.get('shed_id');

    if (!sessionId || !dealerId) {
      setStatus('error');
      setError('Missing payment session details. Contact support if you were charged.');
      return;
    }

    verifyAndFinalizePayment(sessionId, dealerId, shedId);
  }, [location.search]);

  const verifyAndFinalizePayment = async (sessionId, dealerId, shedId) => {
    try {
      // Configurator/custom builds have no inventory row — success_url carries the
      // literal string "null". Normalize so we never query inventory for a ghost id.
      const realShedId = shedId && shedId !== 'null' ? shedId : null;

      // Step 1: Verify the session with Stripe via edge function
      const { data: session, error: sessionError } = await supabase.functions.invoke('stripe-get-session', {
        body: { session_id: sessionId, dealer_id: dealerId }
      });

      if (sessionError || !session) {
        throw new Error('Could not verify payment session');
      }

      if (session.payment_status !== 'paid') {
        throw new Error('Payment was not completed. Please try again.');
      }

      const amountDollars = (session.amount_total / 100);
      const customerEmail = session.customer_email;
      const customerName = session.customer_name || '';
      const paymentType = session.metadata?.payment_type || 'deposit';
      const paymentIntentId = session.payment_intent_id || '';

      // Step 2: Update the payments record from pending → succeeded
      const { data: existingPayment } = await supabase
        .from('payments')
        .select('id, metadata')
        .eq('stripe_session_id', sessionId)
        .single();

      if (existingPayment) {
        await supabase
          .from('payments')
          .update({
            status: 'succeeded',
            stripe_payment_intent_id: paymentIntentId,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingPayment.id);
      } else {
        // No pending record found — create one (edge case: record insert failed earlier)
        await supabase
          .from('payments')
          .insert([{
            dealer_id: dealerId,
            shed_id: realShedId,
            customer_name: customerName,
            customer_email: customerEmail,
            amount: amountDollars,
            payment_type: paymentType,
            status: 'succeeded',
            stripe_session_id: sessionId,
            stripe_payment_intent_id: paymentIntentId,
            processor: 'stripe_connect',
            metadata: {}
          }]);
      }

      // Step 3: Mark the shed as reserved (inventory sheds only)
      if (realShedId) {
        await supabase
          .from('sheds_inventory')
          .update({
            status: 'reserved',
            status_updated_at: new Date().toISOString()
          })
          .eq('id', realShedId);
      }

      // Step 4: Update purchase_agreements status to 'deposit_paid'
      if (realShedId && customerEmail) {
        await supabase
          .from('purchase_agreements')
          .update({
            status: 'deposit_paid',
            payment_session_id: sessionId,
            updated_at: new Date().toISOString()
          })
          .eq('shed_id', realShedId)
          .eq('customer_email', customerEmail)
          .eq('status', 'signed');
      }

      // Step 5: Get dealer info for the email
      const { data: dealer } = await supabase
        .from('dealers')
        .select('company_name, email, phone')
        .eq('id', dealerId)
        .single();

      // Step 6: Get shed info (inventory sheds only; custom builds have no row)
      let shed = null;
      if (realShedId) {
        const { data } = await supabase
          .from('sheds_inventory')
          .select('name, size, shed_type, price, sale_price, deposit_amount')
          .eq('id', realShedId)
          .single();
        shed = data;
      }

      // Custom builds: no inventory price. The deposit is exactly 25% of the
      // configured total, so reconstruct the full price (and store the human
      // name from the pending payment record's metadata).
      const customName = existingPayment?.metadata?.shed_name;
      const isDeposit = paymentType === 'deposit';
      const totalPrice =
        shed?.sale_price ||
        shed?.price ||
        (isDeposit ? Math.round(amountDollars / 0.25) : amountDollars);

      const details = {
        sessionId,
        amount: amountDollars,
        customerEmail,
        customerName,
        paymentType,
        dealerName: dealer?.company_name || 'ShedX Dealer',
        dealerEmail: dealer?.email || '',
        dealerPhone: dealer?.phone || '',
        shedName: shed?.name || customName || 'Custom Shed',
        shedSize: shed?.size || '',
        shedType: shed?.shed_type || '',
        totalPrice,
        balanceDue: Math.max(0, totalPrice - amountDollars)
      };

      setPaymentDetails(details);

      // Step 7: Send confirmation emails
      await sendConfirmationEmails(details, dealerId);

      setStatus('success');
    } catch (err) {
      console.error('Payment verification error:', err);
      setError(err.message || 'Payment verification failed');
      setStatus('error');
    }
  };

  const sendConfirmationEmails = async (details, dealerId) => {
    const receiptDate = new Date().toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });

    // Customer confirmation email
    const customerHtml = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 0; background: #f9fafb;">
        <div style="background: #065f46; padding: 32px; text-align: center;">
          <h1 style="color: #fff; margin: 0; font-size: 24px;">Payment Confirmed</h1>
          <p style="color: #a7f3d0; margin: 8px 0 0; font-size: 14px;">Your ${details.paymentType === 'deposit' ? 'deposit' : 'payment'} has been received</p>
        </div>
        <div style="background: #fff; padding: 32px; border: 1px solid #e5e7eb;">
          <p style="margin: 0 0 16px; color: #374151;">Hi ${details.customerName || 'there'},</p>
          <p style="margin: 0 0 24px; color: #374151;">Your ${details.paymentType === 'deposit' ? 'deposit' : 'payment'} of <strong>$${details.amount.toLocaleString()}</strong> has been processed.</p>
          
          <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 20px; margin-bottom: 24px;">
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 6px 0; color: #6b7280; font-size: 14px;">Item</td>
                <td style="padding: 6px 0; color: #111827; font-size: 14px; text-align: right; font-weight: 600;">${details.shedName || 'Shed'}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; color: #6b7280; font-size: 14px;">Size</td>
                <td style="padding: 6px 0; color: #111827; font-size: 14px; text-align: right;">${details.shedSize}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; color: #6b7280; font-size: 14px;">Type</td>
                <td style="padding: 6px 0; color: #111827; font-size: 14px; text-align: right; text-transform: capitalize;">${details.shedType}</td>
              </tr>
              <tr style="border-top: 1px solid #d1fae5;">
                <td style="padding: 10px 0 6px; color: #6b7280; font-size: 14px;">${details.paymentType === 'deposit' ? 'Deposit paid' : 'Amount paid'}</td>
                <td style="padding: 10px 0 6px; color: #065f46; font-size: 18px; text-align: right; font-weight: 700;">$${details.amount.toLocaleString()}</td>
              </tr>
              ${details.paymentType === 'deposit' ? `
              <tr>
                <td style="padding: 6px 0; color: #6b7280; font-size: 14px;">Total price</td>
                <td style="padding: 6px 0; color: #111827; font-size: 14px; text-align: right;">$${details.totalPrice.toLocaleString()}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; color: #6b7280; font-size: 14px;">Balance due at delivery</td>
                <td style="padding: 6px 0; color: #dc2626; font-size: 14px; text-align: right; font-weight: 600;">$${details.balanceDue.toLocaleString()}</td>
              </tr>` : ''}
            </table>
          </div>

          <p style="margin: 0 0 8px; color: #374151; font-size: 14px;"><strong>Dealer:</strong> ${details.dealerName}</p>
          <p style="margin: 0 0 8px; color: #374151; font-size: 14px;"><strong>Date:</strong> ${receiptDate}</p>
          <p style="margin: 0 0 8px; color: #374151; font-size: 14px;"><strong>Transaction ID:</strong> ${details.sessionId.slice(0, 20)}...</p>

          ${details.paymentType === 'deposit' ? `
          <div style="background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 16px; margin-top: 24px;">
            <p style="margin: 0; color: #92400e; font-size: 14px;"><strong>Next steps:</strong> Your shed is now reserved. The dealer will contact you to arrange delivery. The remaining balance of <strong>$${details.balanceDue.toLocaleString()}</strong> is due at delivery.</p>
          </div>` : ''}
        </div>
        <div style="padding: 16px 32px; text-align: center; color: #9ca3af; font-size: 12px;">
          <p style="margin: 0;">ShedX — Shed Marketplace</p>
          <p style="margin: 4px 0 0;">Questions? Contact ${details.dealerName} at ${details.dealerEmail || 'your dealer'}</p>
        </div>
      </div>
    `;

    // Send to customer
    if (details.customerEmail) {
      try {
        await supabase.functions.invoke('send-email', {
          body: {
            to: details.customerEmail,
            subject: `Payment Confirmed — $${details.amount.toLocaleString()} ${details.paymentType === 'deposit' ? 'Deposit' : 'Payment'} for ${details.shedName || 'Your Shed'}`,
            html: customerHtml
          }
        });
      } catch (emailErr) {
        console.error('Failed to send customer email:', emailErr);
      }
    }

    // Send dealer notification
    if (details.dealerEmail) {
      const dealerHtml = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #1e40af; padding: 24px; text-align: center;">
            <h1 style="color: #fff; margin: 0; font-size: 20px;">New ${details.paymentType === 'deposit' ? 'Deposit' : 'Payment'} Received</h1>
          </div>
          <div style="background: #fff; padding: 24px; border: 1px solid #e5e7eb;">
            <p style="margin: 0 0 16px; font-size: 16px; color: #111827;">You received a <strong>$${details.amount.toLocaleString()}</strong> ${details.paymentType} for <strong>${details.shedName}</strong>.</p>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
              <tr><td style="padding: 6px 0; color: #6b7280;">Customer</td><td style="text-align: right; color: #111827;">${details.customerName}</td></tr>
              <tr><td style="padding: 6px 0; color: #6b7280;">Email</td><td style="text-align: right; color: #111827;">${details.customerEmail}</td></tr>
              <tr><td style="padding: 6px 0; color: #6b7280;">Shed</td><td style="text-align: right; color: #111827;">${details.shedName} (${details.shedSize})</td></tr>
              <tr><td style="padding: 6px 0; color: #6b7280;">Amount</td><td style="text-align: right; color: #065f46; font-weight: 700;">$${details.amount.toLocaleString()}</td></tr>
              ${details.paymentType === 'deposit' ? `<tr><td style="padding: 6px 0; color: #6b7280;">Balance due</td><td style="text-align: right; color: #dc2626; font-weight: 600;">$${details.balanceDue.toLocaleString()}</td></tr>` : ''}
            </table>
            <p style="margin: 0; color: #6b7280; font-size: 13px;">The shed has been marked as reserved. Please reach out to the customer to arrange delivery.</p>
          </div>
        </div>
      `;

      try {
        await supabase.functions.invoke('send-email', {
          body: {
            to: details.dealerEmail,
            subject: `💰 New ${details.paymentType === 'deposit' ? 'Deposit' : 'Payment'}: $${details.amount.toLocaleString()} from ${details.customerName || 'Customer'}`,
            html: dealerHtml
          }
        });
      } catch (emailErr) {
        console.error('Failed to send dealer email:', emailErr);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-16 px-4">
      <div className="max-w-lg w-full">
        {status === 'verifying' && (
          <div className="bg-white rounded-xl shadow-lg p-10 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full mx-auto mb-5" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Verifying your payment...</h1>
            <p className="text-gray-500 text-sm">Please wait while we confirm your transaction with Stripe.</p>
          </div>
        )}

        {status === 'success' && paymentDetails && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Green header */}
            <div className="bg-emerald-600 px-8 py-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheckCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">Payment Successful</h1>
              <p className="text-emerald-100 text-sm">Your {paymentDetails.paymentType === 'deposit' ? 'deposit' : 'payment'} has been processed</p>
            </div>

            {/* Receipt details */}
            <div className="p-8">
              <div className="bg-gray-50 rounded-lg p-5 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">{paymentDetails.paymentType === 'deposit' ? 'Deposit' : 'Payment'}</span>
                  <span className="text-2xl font-bold text-emerald-700">${paymentDetails.amount.toLocaleString()}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Item</span>
                    <span className="text-gray-900 font-medium">{paymentDetails.shedName}</span>
                  </div>
                  {paymentDetails.shedSize && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Size</span>
                      <span className="text-gray-900">{paymentDetails.shedSize}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dealer</span>
                    <span className="text-gray-900">{paymentDetails.dealerName}</span>
                  </div>
                  {paymentDetails.paymentType === 'deposit' && paymentDetails.balanceDue > 0 && (
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span className="text-gray-500">Balance due at delivery</span>
                      <span className="text-red-600 font-semibold">${paymentDetails.balanceDue.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Email confirmation notice */}
              <div className="flex items-start gap-3 bg-blue-50 rounded-lg p-4 mb-6">
                <FiMail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Confirmation email sent</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    A receipt has been emailed to <strong>{paymentDetails.customerEmail}</strong>
                  </p>
                </div>
              </div>

              {/* Next steps for deposit */}
              {paymentDetails.paymentType === 'deposit' && (
                <div className="flex items-start gap-3 bg-amber-50 rounded-lg p-4 mb-6">
                  <FiFileText className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Your shed is now reserved</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {paymentDetails.dealerName} will contact you to arrange delivery. The remaining balance is due at delivery.
                    </p>
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="flex gap-3">
                <Link
                  to="/sheds"
                  className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 rounded-lg transition-colors"
                >
                  <FiHome className="w-4 h-4" />
                  Browse More Sheds
                </Link>
              </div>

              <p className="text-center text-xs text-gray-400 mt-4">
                Transaction: {paymentDetails.sessionId?.slice(0, 28)}...
              </p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-xl shadow-lg p-10 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <FiAlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Verification Issue</h1>
            <p className="text-gray-500 text-sm mb-6">{error}</p>
            <p className="text-xs text-gray-400 mb-6">
              If you were charged, don't worry — your payment is safe. Contact the dealer or email support for help.
            </p>
            <Link
              to="/sheds"
              className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-medium px-6 py-3 rounded-lg transition-colors"
            >
              <FiHome className="w-4 h-4" />
              Back to Sheds
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
